import { Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import PageHero from "../components/sections/PageHero";
import Process from "../components/sections/Process";
import ProjectShowcase from "../components/sections/ProjectShowcase";
import CTASection from "../components/sections/CTASection";
import ContactForm from "../components/sections/ContactForm";
import { WATERPROOFING_TYPES } from "../data/content";

export default function Waterproofing() {
  return (
    <>
      <PageHero
        eyebrow="Service 02 · Waterproofing"
        title="Professional Waterproofing"
        italic="Solutions."
        subtitle="Roofs, terraces, bathrooms, basements, walls, balconies, water tanks and new construction — treatment based on site inspection."
        image="https://customer-assets-jai6qajn.emergentagent.net/job_painter-showcase-v1/artifacts/x4jhbend_exterior-wall-waterproofing-service.png"
      />

      {/* Waterproofing types — 2-col detailed list */}
      <section className="py-16 md:py-24 bg-white">
        <div className="max-w-[1400px] mx-auto px-5 md:px-8">
          <div className="grid grid-cols-1 md:grid-cols-12 gap-8 mb-12">
            <div className="md:col-span-7">
              <div className="font-mono text-[11px] tracking-[0.25em] uppercase text-ink-muted mb-5">
                Waterproofing Services
              </div>
              <h2 className="font-display text-3xl md:text-5xl text-ink leading-tight tracking-[-0.03em]">
                Where water gets in,{" "}
                <span className="font-serif-italic font-normal text-ink-mid">
                  we go find out why.
                </span>
              </h2>
            </div>
            <p className="md:col-span-5 self-end text-ink-muted leading-relaxed">
              Waterproofing done in isolation rarely holds. We inspect the
              source, the extent of the damage and the surrounding structure —
              then choose an appropriate treatment system for the actual site.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-px bg-ink-line border border-ink-line">
            {WATERPROOFING_TYPES.map((w, i) => (
              <article
                key={w.name}
                data-testid={`waterproofing-type-${i}`}
                className="bg-white p-6 md:p-8 flex gap-5"
              >
                <div className="shrink-0 font-mono text-lg text-ink-muted">
                  {String(i + 1).padStart(2, "0")}
                </div>
                <div>
                  <h3 className="font-display text-lg md:text-xl text-ink leading-tight">
                    {w.name}
                  </h3>
                  <p className="mt-2 text-sm text-ink-mid leading-relaxed">
                    {w.note}
                  </p>
                </div>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* Inspection note */}
      <section data-on-dark className="py-16 md:py-24 bg-ink text-white grain relative overflow-hidden border-t border-ink">
        <div className="max-w-[1200px] mx-auto px-5 md:px-8 relative grid grid-cols-1 md:grid-cols-12 gap-8">
          <div className="md:col-span-5">
            <div className="font-mono text-[11px] tracking-[0.25em] uppercase text-white/50 mb-5">
              Site inspection first
            </div>
            <h3 className="font-display text-3xl md:text-5xl leading-tight tracking-[-0.03em]">
              A leakage
              <br />
              <span className="font-serif-italic font-normal text-white/70">
                is not always where it appears.
              </span>
            </h3>
          </div>
          <div className="md:col-span-7 self-end text-white/70 leading-relaxed space-y-4">
            <p>
              Water travels. A stain in a bedroom can start from the terrace,
              an external wall, a bathroom on a floor above, or a plumbing
              line. Treating only the visible symptom is rarely a lasting fix.
            </p>
            <p>
              Urban Painter inspects the actual source of leakage or seepage
              before recommending a suitable waterproofing system.
            </p>
          </div>
        </div>
      </section>

      <Process />
      <ProjectShowcase limit={8} />

      {/* Lead form */}
      <section className="py-16 md:py-24 bg-white border-t border-ink-line">
        <div className="max-w-4xl mx-auto px-5 md:px-8">
          <ContactForm
            defaultService="Waterproofing"
            leadType="site_inspection"
            source="waterproofing_page"
            heading="Book Waterproofing Inspection"
            subheading="Share where the leakage or seepage is showing up. We'll inspect the actual source before quoting."
          />
        </div>
      </section>

      <CTASection />

      <div className="max-w-[1400px] mx-auto px-5 md:px-8 py-10 flex flex-wrap gap-4 justify-between text-sm text-ink-muted">
        <Link to="/epoxy-flooring" className="inline-flex items-center gap-2 hover:text-ink group">
          Also: Epoxy Flooring <ArrowUpRight size={14} className="group-hover:rotate-45 transition-transform" />
        </Link>
        <Link to="/painting" className="inline-flex items-center gap-2 hover:text-ink group">
          Next: Painting Services <ArrowUpRight size={14} className="group-hover:rotate-45 transition-transform" />
        </Link>
      </div>
    </>
  );
}
